import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

interface ForgotPasswordRequest {
  id: number;
  request_type: string;
  request_key: string;
  created_at: string;
}

interface AdminForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  adminUserId: string;
}

import { API_CONFIG } from '../config/api.config';
import { formatStoredDate } from '@/utils/formatStoredDate';

const BACKEND_ORIGIN = API_CONFIG.getBackendOrigin();

export default function AdminForgotPasswordModal({ isOpen, onClose, adminUserId }: AdminForgotPasswordModalProps) {
  const [requests, setRequests] = useState<ForgotPasswordRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [processingId, setProcessingId] = useState<number | null>(null);
  const itemsPerPage = 4;

  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      fetchRequests();
    }
  }, [isOpen]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_ORIGIN}/api/admin/forgot-password-requests`);
      const data = await response.json();
      setRequests(data.requests || []);
    } catch (error) {
      console.error('Failed to fetch forgot password requests:', error);
      alert('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (request: ForgotPasswordRequest) => {
    if (!adminUserId) {
      alert('Admin user ID not found');
      return;
    }

    const confirmed = window.confirm(
      `Reset password for "${request.request_key}" to 123456?`
    );

    if (!confirmed) return;

    setProcessingId(request.id);
    try {
      const response = await fetch(`${BACKEND_ORIGIN}/api/admin/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          requestId: request.id,
          requestKey: request.request_key,
          adminUserId: adminUserId,
        }),
      });

      const result = await response.json();

      if (result.success) {
        alert('Password reset successfully!');
        // Remove the processed request from the list
        setRequests(requests.filter(r => r.id !== request.id));
      } else {
        alert(result.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Failed to reset password:', error);
      alert('Failed to reset password');
    } finally {
      setProcessingId(null);
    }
  };

  const filteredRequests = useMemo(() => {
    if (!searchTerm) return requests;
    const search = searchTerm.toLowerCase();
    return requests.filter(
      r =>
        r.request_key.toLowerCase().includes(search) ||
        r.request_type.toLowerCase().includes(search)
    );
  }, [requests, searchTerm]);

  const paginatedRequests = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredRequests.slice(startIndex, endIndex);
  }, [filteredRequests, currentPage]);

  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleReset_Search = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  if (!isOpen) return null;

  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, filteredRequests.length);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        backdropFilter: 'blur(6px)',
      }}
    >
      <div className="relative w-full max-w-6xl mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div
          className="px-8 py-6 text-white"
          style={{
            background: 'var(--card-gradient-total, linear-gradient(135deg, #667eea 0%, #764ba2 100%))',
          }}
        >
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">Forgot Password Requests</h2>
            <div className="flex gap-3">
              <button
                onClick={handleReset_Search}
                className="px-5 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-all"
              >
                Reset
              </button>
              <button
                onClick={onClose}
                className="px-5 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-all"
              >
                Close
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="mt-4">
            <input
              type="text"
              placeholder="Search by request key or type..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 bg-white/20 backdrop-blur-sm text-white placeholder-white/70 rounded-lg border border-white/30 focus:outline-none focus:ring-2 focus:ring-white/50"
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              <p className="mt-4 text-gray-600">Loading requests...</p>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No pending forgot password requests found.</p>
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead
                    style={{
                      background: 'var(--card-gradient-total, linear-gradient(135deg, #667eea 0%, #764ba2 100%))',
                    }}
                  >
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">
                        S.No
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">
                        Request Type
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">
                        Request Key
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">
                        Created At
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-white uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedRequests.map((request, idx) => (
                      <tr key={request.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {startIndex + idx}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                          {request.request_type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                          {request.request_key}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatStoredDate(request.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <button
                            onClick={() => handleReset(request)}
                            disabled={processingId === request.id}
                            className={`px-4 py-2 rounded-lg font-medium transition-all ${
                              processingId === request.id
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white shadow-md hover:shadow-lg'
                            }`}
                          >
                            {processingId === request.id ? 'Processing...' : 'Reset Password'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {startIndex}–{endIndex} of {filteredRequests.length} requests
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handlePrevious}
                    disabled={currentPage === 1}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      currentPage === 1
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-purple-600 text-white hover:bg-purple-700 shadow-md hover:shadow-lg'
                    }`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={handleNext}
                    disabled={currentPage === totalPages}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      currentPage === totalPages
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-purple-600 text-white hover:bg-purple-700 shadow-md hover:shadow-lg'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
